import React, { useState, useEffect, useRef } from 'react';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import ErrorModal from '../components/modals/ErrorModal';
import SuccessModal from '../components/modals/SuccessModal';
import './GroupDiagram.css';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 3;
const ZOOM_WHEEL_STEP = 0.08;

const GroupDiagram = () => {
  const [allGroups, setAllGroups] = useState([]);
  const [allGroupsPeriods, setAllGroupsPeriods] = useState([]);
  const [allRelationships, setAllRelationships] = useState([]);
  const [allPeriods, setAllPeriods] = useState([]);
  const [allForms, setAllForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState('force'); // 'force', 'hierarchical'
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState(new Set()); // Multi-select: Set of node IDs
  const [draggingNode, setDraggingNode] = useState(null);
  const [draggingNodes, setDraggingNodes] = useState(new Set()); // Multi-drag: Set of node IDs being dragged
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPositions, setDragStartPositions] = useState({}); // Store initial positions when drag starts
  const [isAreaSelecting, setIsAreaSelecting] = useState(false);
  const [areaSelectStart, setAreaSelectStart] = useState({ x: 0, y: 0 });
  const [areaSelectEnd, setAreaSelectEnd] = useState({ x: 0, y: 0 });
  const [nodePositions, setNodePositions] = useState({}); // Store manual positions: { nodeId: { x, y } }
  const [forceLayoutEnabled, setForceLayoutEnabled] = useState(true);
  const [isSavingPositions, setIsSavingPositions] = useState(false);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1.5);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [panSensitivity, setPanSensitivity] = useState(3);
  const [positionHistory, setPositionHistory] = useState([]); // Array of position snapshots
  const [historyIndex, setHistoryIndex] = useState(-1); // Current position in history (-1 means at the end)
  const svgRef = useRef(null);
  const diagramContainerRef = useRef(null);

  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

  const POSITIONS_TITLE = 'group_diagram_positions';

  useEffect(() => {
    loadAllData();
    loadSavedPositions();
  }, []);


  const loadAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [groupsRes, groupsPeriodsRes, relationshipsRes, periodsRes, formsRes] = await Promise.all([
        fetch('/api/groups'),
        fetch('/api/groups-periods'),
        fetch('/api/relationships'),
        fetch('/api/periods'),
        fetch('/api/forms')
      ]);

      const [groupsData, groupsPeriodsData, relationshipsData, periodsData, formsData] = await Promise.all([
        groupsRes.json(),
        groupsPeriodsRes.json(),
        relationshipsRes.json(),
        periodsRes.json(),
        formsRes.json()
      ]);

      if (groupsData.success) {
        setAllGroups(groupsData.data);
        console.log('Groups loaded:', groupsData.data.length);
      } else {
        console.error('Failed to load groups:', groupsData);
      }
      
      if (groupsPeriodsData.success) {
        setAllGroupsPeriods(groupsPeriodsData.data);
        console.log('Groups-periods loaded:', groupsPeriodsData.data.length);
      } else {
        console.error('Failed to load groups-periods:', groupsPeriodsData);
      }
      
      if (relationshipsData.success) {
        setAllRelationships(relationshipsData.data);
        console.log('Relationships loaded:', relationshipsData.data.length);
      } else {
        console.error('Failed to load relationships:', relationshipsData);
      }
      
      if (periodsData.success) {
        setAllPeriods(periodsData.data);
        console.log('Periods loaded:', periodsData.data.length);
      } else {
        console.error('Failed to load periods:', periodsData);
      }

      if (formsData.success) {
        setAllForms(formsData.data);
        console.log('Forms loaded:', formsData.data.length);
      } else {
        console.error('Failed to load forms:', formsData);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  // Get unique periods for a group
  const getPeriodsForGroup = (groupId) => {
    const groupPeriods = allGroupsPeriods.filter(gp => gp.group_id === groupId);
    const periodIds = [...new Set(groupPeriods.map(gp => gp.period_id))];
    return periodIds.map(pid => allPeriods.find(p => p.id === pid)).filter(Boolean);
  };

  // Get child groups through relationships (where parent is groups_periods)
  const getChildGroups = (groupPeriodId) => {
    const childRelationships = allRelationships.filter(
      r => r.parent_table === 'groups_periods' && 
           r.parent_id === groupPeriodId && 
           r.child_table === 'groups_periods'
    );
    
    const childGroupPeriodIds = childRelationships.map(r => r.child_id);
    const childGroupPeriods = allGroupsPeriods.filter(gp => childGroupPeriodIds.includes(gp.id));
    const childGroupIds = [...new Set(childGroupPeriods.map(gp => gp.group_id))];
    return childGroupIds.map(gid => allGroups.find(g => g.id === gid)).filter(Boolean);
  };

  // Get parent groups through relationships
  const getParentGroups = (groupPeriodId) => {
    const parentRelationships = allRelationships.filter(
      r => r.child_table === 'groups_periods' && 
           r.child_id === groupPeriodId && 
           r.parent_table === 'groups_periods'
    );
    
    const parentGroupPeriodIds = parentRelationships.map(r => r.parent_id);
    const parentGroupPeriods = allGroupsPeriods.filter(gp => parentGroupPeriodIds.includes(gp.id));
    const parentGroupIds = [...new Set(parentGroupPeriods.map(gp => gp.group_id))];
    return parentGroupIds.map(gid => allGroups.find(g => g.id === gid)).filter(Boolean);
  };

  // Calculate graph structure - memoize with dependencies
  const graph = React.useMemo(() => {
    console.log('Calculating graph with:', {
      groups: allGroups.length,
      groupsPeriods: allGroupsPeriods.length,
      relationships: allRelationships.length,
      periods: allPeriods.length,
      forms: allForms.length
    });

    // Extract unique groups from groups-periods if groups array is empty
    let groupsToUse = allGroups;
    if (!allGroups.length && allGroupsPeriods.length > 0) {
      const groupIds = [...new Set(allGroupsPeriods.map(gp => gp.group_id))];
      groupsToUse = groupIds.map(groupId => {
        const firstGp = allGroupsPeriods.find(gp => gp.group_id === groupId);
        return {
          id: groupId,
          name: firstGp?.group_name || `Group ${groupId}`,
          description: firstGp?.group_description || '',
          group_type: firstGp?.group_type || 'group',
          created_at: firstGp?.group_created_at || firstGp?.created_at,
          updated_at: firstGp?.group_updated_at || firstGp?.updated_at
        };
      });
      console.log('Extracted groups from groups-periods:', groupsToUse.length);
    }

    if (!groupsToUse.length || !allGroupsPeriods.length) {
      console.log('Not enough data for graph - returning empty');
      return { nodes: [], edges: [] };
    }

    const nodes = [];
    const edges = [];
    const nodeMap = new Map();

    // Helper function to get periods for a group
    const getPeriodsForGroupLocal = (groupId) => {
      const groupPeriods = allGroupsPeriods.filter(gp => gp.group_id === groupId);
      const periodIds = [...new Set(groupPeriods.map(gp => gp.period_id))];
      return periodIds.map(pid => allPeriods.find(p => p.id === pid)).filter(Boolean);
    };

    // Create nodes for each groups_periods entry (not just groups)
    // This way we can show relationships between specific group-period combinations
    const groupsPeriodsNodesMap = new Map();
    const groupNodesMap = new Map();

    // First, add group nodes for visual organization
    groupsToUse.forEach((group, idx) => {
      const nodeId = `group-${group.id}`;
      const node = {
        id: nodeId,
        type: 'group',
        data: group,
        x: 200,
        y: 200,
        width: 200,
        height: 90,
        vx: 0,
        vy: 0,
        isContainer: true
      };
      nodes.push(node);
      nodeMap.set(nodeId, node);
      groupNodesMap.set(group.id, node);
    });

    // Add nodes for each groups_periods entry
    allGroupsPeriods.forEach(gp => {
      const groupsPeriodsNodeId = `groupsperiods-${gp.id}`;
      const groupNode = groupNodesMap.get(gp.group_id);
      const period = allPeriods.find(p => p.id === gp.period_id);
      
      if (!groupsPeriodsNodesMap.has(groupsPeriodsNodeId)) {
        const node = {
          id: groupsPeriodsNodeId,
          type: 'groups_periods',
          data: gp,
          groupId: gp.group_id,
          periodId: gp.period_id,
          groupsPeriodsId: gp.id,
          x: (groupNode?.x || 200) + 250,
          y: (groupNode?.y || 200),
          width: 170,
          height: 70,
          vx: 0,
          vy: 0
        };
        nodes.push(node);
        nodeMap.set(groupsPeriodsNodeId, node);
        groupsPeriodsNodesMap.set(groupsPeriodsNodeId, node);
        
        // Add edge from group to groups_periods (visual grouping) with initial spacing
        if (groupNode) {
          // Adjust initial position to have more spacing
          node.x = (groupNode.x || 200) + 350;
          
          edges.push({
            id: `edge-group-groupsperiods-${gp.group_id}-${gp.id}`,
            source: `group-${gp.group_id}`,
            target: groupsPeriodsNodeId,
            type: 'has_groups_periods',
            isGroupPeriod: true,
            isVisual: true // This is just for visual grouping
          });
        }
      }
    });

    // Add relationship edges between groups_periods entries (not groups!)
    const groupPeriodsRelationships = allRelationships.filter(
      r => r.parent_table === 'groups_periods' && r.child_table === 'groups_periods'
    );

    groupPeriodsRelationships.forEach(rel => {
      const parentGpId = `groupsperiods-${rel.parent_id}`;
      const childGpId = `groupsperiods-${rel.child_id}`;
      
      // Only add edge if both groups_periods nodes exist
      if (nodeMap.has(parentGpId) && nodeMap.has(childGpId)) {
        edges.push({
          id: `edge-relationship-${rel.id}`,
          source: parentGpId,
          target: childGpId,
          type: rel.relationship_type || 'contains',
          data: rel,
          isGroupPeriod: false,
          isVisual: false // This is a real relationship
        });
      }
    });

    // Add form nodes and connect them to groups_periods
    const formNodesMap = new Map();
    const formRelationships = allRelationships.filter(
      r => r.parent_table === 'groups_periods' && r.child_table === 'forms'
    );

    formRelationships.forEach(rel => {
      const form = allForms.find(f => f.id === rel.child_id);
      if (!form) return;

      const groupsPeriodsNodeId = `groupsperiods-${rel.parent_id}`;
      const formNodeId = `form-${form.id}`;
      
      // Create form node if it doesn't exist
      if (!formNodesMap.has(formNodeId)) {
        const groupsPeriodsNode = nodeMap.get(groupsPeriodsNodeId);
        const formNode = {
          id: formNodeId,
          type: 'form',
          data: form,
          formId: form.id,
          x: (groupsPeriodsNode?.x || 200) + 400,
          y: (groupsPeriodsNode?.y || 200),
          width: 150,
          height: 60,
          vx: 0,
          vy: 0
        };
        nodes.push(formNode);
        nodeMap.set(formNodeId, formNode);
        formNodesMap.set(formNodeId, formNode);
      }

      // Add edge from groups_periods to form
      if (nodeMap.has(groupsPeriodsNodeId)) {
        edges.push({
          id: `edge-form-${rel.id}`,
          source: groupsPeriodsNodeId,
          target: formNodeId,
          type: rel.relationship_type || 'has_form',
          data: rel,
          isForm: true,
          isVisual: false
        });
      }
    });

    // Organize nodes by relationship status before applying force layout
    const groupsPeriodsWithRelationships = new Set();
    edges.forEach(edge => {
      if (!edge.isGroupPeriod && !edge.isVisual) {
        groupsPeriodsWithRelationships.add(edge.source);
        groupsPeriodsWithRelationships.add(edge.target);
      }
    });

    // Separate groups_periods into those with relationships and those without
    const relatedGroupsPeriods = [];
    const isolatedGroupsPeriods = [];

    nodes.forEach(node => {
      if (node.type === 'groups_periods') {
        if (groupsPeriodsWithRelationships.has(node.id)) {
          relatedGroupsPeriods.push(node.id);
        } else {
          isolatedGroupsPeriods.push(node.id);
        }
      }
    });

    // Initial positioning: cluster isolated groups_periods in one area, related ones in another
    const isolatedStartX = 250;
    const isolatedStartY = 250;
    const isolatedPerRow = Math.ceil(Math.sqrt(Math.max(1, isolatedGroupsPeriods.length)));
    const isolatedSpacingX = 400; // Increased horizontal spacing
    const isolatedSpacingY = 350; // Increased vertical spacing
    
    isolatedGroupsPeriods.forEach((nodeId, idx) => {
      const node = nodeMap.get(nodeId);
      if (node) {
        const row = Math.floor(idx / isolatedPerRow);
        const col = idx % isolatedPerRow;
        node.x = isolatedStartX + col * isolatedSpacingX;
        node.y = isolatedStartY + row * isolatedSpacingY;
        
        // Position parent group node nearby with more space
        const groupNode = groupNodesMap.get(node.groupId);
        if (groupNode) {
          groupNode.x = node.x - 350;
          groupNode.y = node.y;
        }
      }
    });

    const relatedStartX = 1100;
    const relatedStartY = 250;
    const relatedPerRow = Math.ceil(Math.sqrt(Math.max(1, relatedGroupsPeriods.length)));
    const relatedSpacingX = 400; // Increased horizontal spacing
    const relatedSpacingY = 350; // Increased vertical spacing
    
    relatedGroupsPeriods.forEach((nodeId, idx) => {
      const node = nodeMap.get(nodeId);
      if (node) {
        const row = Math.floor(idx / relatedPerRow);
        const col = idx % relatedPerRow;
        node.x = relatedStartX + col * relatedSpacingX;
        node.y = relatedStartY + row * relatedSpacingY;
        
        // Position parent group node nearby with more space
        const groupNode = groupNodesMap.get(node.groupId);
        if (groupNode) {
          groupNode.x = node.x - 350;
          groupNode.y = node.y;
        }
      }
    });

    // Position remaining groups_periods that weren't categorized (shouldn't happen but just in case)
    nodes.forEach(node => {
      if (node.type === 'groups_periods' && !isolatedGroupsPeriods.includes(node.id) && !relatedGroupsPeriods.includes(node.id)) {
        const groupNode = groupNodesMap.get(node.groupId);
        if (groupNode) {
          node.x = groupNode.x + 280;
          node.y = groupNode.y;
        }
      }
    });

    // Apply force-directed layout if enabled
    if (layout === 'force') {
      // Improved force simulation with clustering awareness - more iterations for better spacing
      for (let iter = 0; iter < 200; iter++) {
        nodes.forEach(node => {
          node.vx = 0;
          node.vy = 0;
        });

        // Strong repulsion to prevent overlapping (increased minimum distances)
        nodes.forEach((node, idx) => {
          nodes.forEach((other, oidx) => {
            if (idx === oidx) return;
            const dx = node.x - other.x;
            const dy = node.y - other.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            
            // Increased minimum distances based on node types
            let minDistance = 250; // Default minimum
            if (node.type === 'group' && other.type === 'group') {
              minDistance = 300; // Groups need more space
            } else if (node.type === 'groups_periods' && other.type === 'groups_periods') {
              minDistance = 280; // Groups_periods need good spacing
            } else if ((node.type === 'group' && other.type === 'groups_periods') || 
                       (node.type === 'groups_periods' && other.type === 'group')) {
              minDistance = 320; // Between group and its groups_periods
            } else if (node.type === 'form' || other.type === 'form') {
              minDistance = 200; // Forms can be closer
            }
            
            if (dist < minDistance) {
              // Much stronger repulsion when too close - prevent overlaps
              const force = (minDistance - dist) * 4.5;
              node.vx += (dx / dist) * force;
              node.vy += (dy / dist) * force;
            } else {
              // Stronger standard repulsion
              const force = 5000 / (dist * dist);
              node.vx += (dx / dist) * force;
              node.vy += (dy / dist) * force;
            }
          });
        });

        // Clustering force for isolated groups_periods (attract to their area)
        if (isolatedGroupsPeriods.length > 1) {
          const isolatedCenterX = isolatedGroupsPeriods.reduce((sum, id) => {
            const n = nodeMap.get(id);
            return sum + (n?.x || 0);
          }, 0) / isolatedGroupsPeriods.length;
          const isolatedCenterY = isolatedGroupsPeriods.reduce((sum, id) => {
            const n = nodeMap.get(id);
            return sum + (n?.y || 0);
          }, 0) / isolatedGroupsPeriods.length;

          isolatedGroupsPeriods.forEach(nodeId => {
            const node = nodeMap.get(nodeId);
            if (node) {
              const dx = isolatedCenterX - node.x;
              const dy = isolatedCenterY - node.y;
              const dist = Math.sqrt(dx * dx + dy * dy) || 1;
              const force = dist * 0.02; // Gentle clustering
              node.vx += (dx / dist) * force;
              node.vy += (dy / dist) * force;
            }
          });
        }

        // Attraction along edges with increased ideal distances
        edges.forEach(edge => {
          const sourceNode = nodeMap.get(edge.source);
          const targetNode = nodeMap.get(edge.target);
          
          if (sourceNode && targetNode) {
            const dx = targetNode.x - sourceNode.x;
            const dy = targetNode.y - sourceNode.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            // Increased ideal distances - visual edges shorter, relationships longer
            const idealDistance = edge.isForm ? 250 : edge.isVisual ? 350 : edge.isGroupPeriod ? 320 : 450;
            const force = (dist - idealDistance) * 0.012; // Slightly weaker to allow more spacing
            
            sourceNode.vx += (dx / dist) * force;
            sourceNode.vy += (dy / dist) * force;
            targetNode.vx -= (dx / dist) * force;
            targetNode.vy -= (dy / dist) * force;
          }
        });

        // Keep groups_periods nodes near their parent group nodes with more spacing
        groupsToUse.forEach(group => {
          const groupNodeId = `group-${group.id}`;
          const groupNode = nodeMap.get(groupNodeId);
          if (!groupNode) return;
          
          // Find all groups_periods nodes for this group
          const groupPeriodsNodes = nodes.filter(n => n.type === 'groups_periods' && n.groupId === group.id);
          groupPeriodsNodes.forEach((gpNode, idx) => {
            // Increased spacing between group and groups_periods, and between multiple groups_periods
            const idealX = groupNode.x + 380;
            const idealY = groupNode.y + idx * 130;
            const dx = idealX - gpNode.x;
            const dy = idealY - gpNode.y;
            const forceX = dx * 0.08; // Slightly weaker to allow more flexibility
            const forceY = dy * 0.08;
            gpNode.vx += forceX;
            gpNode.vy += forceY;
          });
        });

        // Keep form nodes near their parent groups_periods nodes
        allGroupsPeriods.forEach(gp => {
          const groupsPeriodsNodeId = `groupsperiods-${gp.id}`;
          const groupsPeriodsNode = nodeMap.get(groupsPeriodsNodeId);
          if (!groupsPeriodsNode) return;

          // Find all forms connected to this groups_periods
          const formRelationshipsForGp = allRelationships.filter(
            r => r.parent_table === 'groups_periods' && r.parent_id === gp.id && r.child_table === 'forms'
          );
          
          formRelationshipsForGp.forEach((rel, idx) => {
            const formNodeId = `form-${rel.child_id}`;
            const formNode = nodeMap.get(formNodeId);
            if (!formNode) return;

            // Position forms to the right of their groups_periods with vertical spacing
            const idealX = groupsPeriodsNode.x + 250;
            const idealY = groupsPeriodsNode.y + idx * 80;
            const dx = idealX - formNode.x;
            const dy = idealY - formNode.y;
            const forceX = dx * 0.1;
            const forceY = dy * 0.1;
            formNode.vx += forceX;
            formNode.vy += forceY;
          });
        });

        // Update positions with damping (only if force layout is enabled and node isn't manually positioned)
        nodes.forEach(node => {
          // Skip force layout for manually positioned nodes (but not the one being dragged)
          if (nodePositions[node.id] && draggingNode !== node.id) {
            node.x = nodePositions[node.id].x;
            node.y = nodePositions[node.id].y;
            return;
          }

          if (forceLayoutEnabled) {
            node.x += node.vx * 0.15; // Slightly faster movement
            node.y += node.vy * 0.15;
          }
          
          // Larger boundary constraints to allow more space
          const margin = node.type === 'group' ? 120 : 100;
          node.x = Math.max(margin, Math.min(1800 - margin, node.x));
          node.y = Math.max(margin, Math.min(1100 - margin, node.y));
        });
      }
    }

    // Apply manual positions if they exist (for all nodes, including the one being dragged)
    nodes.forEach(node => {
      if (nodePositions[node.id]) {
        // Always use the saved position, even when dragging (it's updated in real-time)
        node.x = nodePositions[node.id].x;
        node.y = nodePositions[node.id].y;
      }
    });

    return { nodes, edges };
  }, [allGroups, allGroupsPeriods, allRelationships, allPeriods, allForms, layout, nodePositions, draggingNode, forceLayoutEnabled]);

  const canvasReady = !isLoading && graph.nodes.length > 0;

  // Bind wheel zoom after canvas mounts (initial effect ran before the canvas existed)
  useEffect(() => {
    if (!canvasReady) return;

    let targets = [];
    let cancelled = false;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const direction = e.deltaY > 0 ? -1 : 1;
      setZoomLevel((prev) => clampZoom(prev + direction * ZOOM_WHEEL_STEP));
    };

    const opts = { passive: false, capture: true };

    const attach = () => {
      if (cancelled) return;
      targets = [diagramContainerRef.current, svgRef.current].filter(Boolean);
      targets.forEach((el) => el.addEventListener('wheel', onWheel, opts));
    };

    const frame = requestAnimationFrame(attach);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      targets.forEach((el) => el.removeEventListener('wheel', onWheel, opts));
    };
  }, [canvasReady]);

  const handleNodeClick = (node, e) => {
    // Don't select if we're dragging
    if (draggingNode || draggingNodes.size > 0) return;
    
    // Ctrl/Cmd + click for multi-select
    if (e.ctrlKey || e.metaKey) {
      setSelectedNodes(prev => {
        const newSet = new Set(prev);
        if (newSet.has(node.id)) {
          newSet.delete(node.id);
        } else {
          newSet.add(node.id);
        }
        return newSet;
      });
      // Also update single selected node for details panel
      setSelectedNode(node);
    } else {
      // Regular click - single select
      setSelectedNodes(new Set([node.id]));
      setSelectedNode(node);
    }
  };

  // Get SVG coordinates from mouse event (in node coordinate space, accounting for pan and zoom)
  const getSVGCoordinates = (e) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const svg = svgRef.current;
    
    // Get the transformed group element (the one with the pan/zoom transform)
    const transformedGroup = svg.querySelector('g[transform*="translate"]');
    if (!transformedGroup) {
      // Fallback if group not found
      const rect = svg.getBoundingClientRect();
      const viewBox = svg.viewBox.baseVal;
      const scaleX = viewBox.width / rect.width;
      const scaleY = viewBox.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    }
    
    // Create a point in screen coordinates
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    
    // Get the CTM (Current Transformation Matrix) of the transformed group
    // This gives us the matrix from the group's coordinate space to screen space
    const groupCTM = transformedGroup.getScreenCTM();
    if (!groupCTM) return { x: 0, y: 0 };
    
    // Invert the matrix to convert from screen space to the group's coordinate space
    const inverseCTM = groupCTM.inverse();
    const groupPoint = pt.matrixTransform(inverseCTM);
    
    return { x: groupPoint.x, y: groupPoint.y };
  };

  const isInteractiveDiagramTarget = (target) => {
    const tag = target?.tagName?.toLowerCase();
    return ['rect', 'text', 'circle', 'line', 'polygon', 'path', 'ellipse'].includes(tag);
  };

  const isDiagramBackgroundTarget = (target) => {
    if (!target || isInteractiveDiagramTarget(target)) return false;
    if (target === svgRef.current) return true;
    const tag = target.tagName?.toLowerCase();
    return tag === 'svg' || tag === 'g';
  };

  const handleNodeMouseDown = (e, node) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    
    const svgCoords = getSVGCoordinates(e);
    
    // If clicking on a node that's not in the selection, select it (unless Ctrl is held)
    if (!e.ctrlKey && !e.metaKey && !selectedNodes.has(node.id)) {
      setSelectedNodes(new Set([node.id]));
      setSelectedNode(node);
    }
    
    // Determine which nodes to drag
    const nodesToDrag = selectedNodes.has(node.id) && selectedNodes.size > 1 
      ? selectedNodes 
      : new Set([node.id]);
    
    // Save positions for all nodes being dragged
    setNodePositions(prev => {
      const newPositions = { ...prev };
      nodesToDrag.forEach(nodeId => {
        const nodeToSave = graph.nodes.find(n => n.id === nodeId);
        if (nodeToSave && !newPositions[nodeId]) {
          newPositions[nodeId] = { x: nodeToSave.x, y: nodeToSave.y };
        }
      });
      return newPositions;
    });
    
    // Store initial positions of all nodes being dragged
    const initialPositions = {};
    nodesToDrag.forEach(nodeId => {
      const nodeToSave = graph.nodes.find(n => n.id === nodeId);
      if (nodeToSave) {
        initialPositions[nodeId] = { x: nodeToSave.x, y: nodeToSave.y };
      }
    });
    setDragStartPositions(initialPositions);
    
    setDraggingNode(node.id);
    setDraggingNodes(nodesToDrag);
    setDragOffset({
      x: svgCoords.x - node.x,
      y: svgCoords.y - node.y
    });
  };

  // Left-drag on background: pan. Right-drag on background: area selection.
  const handleSVGMouseDown = (e) => {
    if (!isDiagramBackgroundTarget(e.target)) return;

    if (e.button === 2) {
      e.preventDefault();
      const svgCoords = getSVGCoordinates(e);
      setIsAreaSelecting(true);
      setAreaSelectStart(svgCoords);
      setAreaSelectEnd(svgCoords);
      return;
    }

    if (e.button === 0) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      setSelectedNodes(new Set());
      setSelectedNode(null);
    }
  };

  const handleDiagramContextMenu = (e) => {
    e.preventDefault();
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (!draggingNode && draggingNodes.size === 0 && !isAreaSelecting && !isPanning) return;

    const handleMouseMove = (e) => {
      if (isPanning) {
        // Panning - update pan offset based on mouse movement with sensitivity
        const deltaX = e.clientX - panStart.x;
        const deltaY = e.clientY - panStart.y;
        setPanOffset(prev => ({
          x: prev.x + (deltaX / zoomLevel) * panSensitivity,
          y: prev.y + (deltaY / zoomLevel) * panSensitivity
        }));
        setPanStart({ x: e.clientX, y: e.clientY });
      } else if (isAreaSelecting) {
        // Area selection - update selection in real-time
        const svgCoords = getSVGCoordinates(e);
        setAreaSelectEnd(svgCoords);
        
        const minX = Math.min(areaSelectStart.x, svgCoords.x);
        const maxX = Math.max(areaSelectStart.x, svgCoords.x);
        const minY = Math.min(areaSelectStart.y, svgCoords.y);
        const maxY = Math.max(areaSelectStart.y, svgCoords.y);
        
        // Find nodes within selection area using current graph
        const nodesInArea = graph.nodes.filter(node => {
          const nodeRight = node.x + node.width;
          const nodeBottom = node.y + node.height;
          return node.x < maxX && nodeRight > minX && node.y < maxY && nodeBottom > minY;
        });
        
        setSelectedNodes(new Set(nodesInArea.map(n => n.id)));
        if (nodesInArea.length > 0) {
          setSelectedNode(nodesInArea[0]);
        }
      } else if (draggingNode || draggingNodes.size > 0) {
        // Dragging nodes - calculate delta from initial drag position
        const svgCoords = getSVGCoordinates(e);
        const nodesToMove = draggingNodes.size > 0 ? draggingNodes : new Set([draggingNode]);
        
        // Get the primary node's initial position
        const primaryInitial = dragStartPositions[draggingNode];
        if (!primaryInitial) return;
        
        // Calculate where the primary node should be based on mouse position
        const primaryNewX = svgCoords.x - dragOffset.x;
        const primaryNewY = svgCoords.y - dragOffset.y;
        
        // Calculate delta from initial position
        const deltaX = primaryNewX - primaryInitial.x;
        const deltaY = primaryNewY - primaryInitial.y;
        
        // Update positions for all dragged nodes
        setNodePositions(prev => {
          const newPositions = { ...prev };
          nodesToMove.forEach(nodeId => {
            const initialPos = dragStartPositions[nodeId];
            if (initialPos) {
              const newX = initialPos.x + deltaX;
              const newY = initialPos.y + deltaY;
              
              // Boundary constraints
              const margin = 50;
              const constrainedX = Math.max(margin, Math.min(2000 - margin, newX));
              const constrainedY = Math.max(margin, Math.min(1200 - margin, newY));
              
              newPositions[nodeId] = { x: constrainedX, y: constrainedY };
            }
          });
          return newPositions;
        });
      }
    };

    const handleMouseUp = () => {
      if (isPanning) {
        setIsPanning(false);
      }
      if (isAreaSelecting) {
        // Finalize area selection
        const minX = Math.min(areaSelectStart.x, areaSelectEnd.x);
        const maxX = Math.max(areaSelectStart.x, areaSelectEnd.x);
        const minY = Math.min(areaSelectStart.y, areaSelectEnd.y);
        const maxY = Math.max(areaSelectStart.y, areaSelectEnd.y);
        
        // Find nodes in the selection area using current graph
        const nodesInArea = graph.nodes.filter(node => {
          const nodeRight = node.x + node.width;
          const nodeBottom = node.y + node.height;
          return node.x < maxX && nodeRight > minX && node.y < maxY && nodeBottom > minY;
        });
        
        setSelectedNodes(new Set(nodesInArea.map(n => n.id)));
        if (nodesInArea.length > 0) {
          setSelectedNode(nodesInArea[0]);
        }
        
        setIsAreaSelecting(false);
        setAreaSelectStart({ x: 0, y: 0 });
        setAreaSelectEnd({ x: 0, y: 0 });
      }
      if (draggingNode || draggingNodes.size > 0) {
        // Save current positions to history when drag ends
        saveToHistory(nodePositions);
        setDraggingNode(null);
        setDraggingNodes(new Set());
        setDragOffset({ x: 0, y: 0 });
        setDragStartPositions({});
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingNode, draggingNodes, dragOffset, isAreaSelecting, isPanning, areaSelectStart, areaSelectEnd, panStart, zoomLevel, panSensitivity, dragStartPositions, graph.nodes]);

  const getNodeColor = (type) => {
    switch (type) {
      case 'group':
        return '#3b82f6';
      case 'groups_periods':
        return '#8b5cf6';
      case 'period':
        return '#10b981';
      case 'form':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  // Get forms for a specific groups_periods
  const getFormsForGroupsPeriods = (groupsPeriodsId) => {
    return allRelationships
      .filter(r => r.parent_table === 'groups_periods' && 
                   r.parent_id === groupsPeriodsId && 
                   r.child_table === 'forms')
      .map(rel => {
        const form = allForms.find(f => f.id === rel.child_id);
        return form ? { ...form, relationship_type: rel.relationship_type } : null;
      })
      .filter(Boolean);
  };

  // Save current positions to history
  const saveToHistory = (positions) => {
    const positionsCopy = JSON.parse(JSON.stringify(positions)); // Deep copy
    setPositionHistory(prev => {
      // If we're not at the end of history, remove everything after current index
      const newHistory = historyIndex < prev.length - 1 
        ? prev.slice(0, historyIndex + 1)
        : prev;
      // Add new snapshot and limit to 50 entries
      const updated = [...newHistory, positionsCopy];
      return updated.slice(-50); // Keep only last 50 entries
    });
    setHistoryIndex(prev => {
      const newIndex = prev + 1;
      // Limit history index to 49 (since we keep max 50 entries)
      return Math.min(newIndex, 49);
    });
  };

  // Undo last position change
  const undo = () => {
    setPositionHistory(prev => {
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setNodePositions(JSON.parse(JSON.stringify(prev[newIndex])));
      }
      return prev;
    });
  };

  // Redo last undone position change
  const redo = () => {
    setPositionHistory(prev => {
      if (historyIndex < prev.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setNodePositions(JSON.parse(JSON.stringify(prev[newIndex])));
      }
      return prev;
    });
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [historyIndex, positionHistory]);

  // Load saved positions from database
  const loadSavedPositions = async () => {
    setIsLoadingPositions(true);
    try {
      const response = await fetch(`/api/miscellaneous/${encodeURIComponent(POSITIONS_TITLE)}`);
      const data = await response.json();
      
      if (data.success && data.data && data.data.data) {
        const positions = data.data.data;
        setNodePositions(positions);
        // Initialize history with loaded positions
        setPositionHistory([positions]);
        setHistoryIndex(0);
        // Disable force layout if we have saved positions
        if (Object.keys(positions).length > 0) {
          setForceLayoutEnabled(false);
        }
        console.log('Loaded saved positions:', positions);
      } else {
        console.log('No saved positions found');
        // Initialize with empty history
        setPositionHistory([{}]);
        setHistoryIndex(0);
      }
    } catch (error) {
      console.error('Failed to load saved positions:', error);
      // Initialize with empty history on error
      setPositionHistory([{}]);
      setHistoryIndex(0);
    } finally {
      setIsLoadingPositions(false);
    }
  };

  // Save positions to database
  const savePositions = async () => {
    setIsSavingPositions(true);
    try {
      const response = await fetch(`/api/miscellaneous/${encodeURIComponent(POSITIONS_TITLE)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: nodePositions
        }),
      });

      const data = await response.json();

      if (data.success) {
        setModalData({
          title: "Positions Saved!",
          message: "Node positions have been saved successfully.",
          details: `Saved ${Object.keys(nodePositions).length} node positions.`
        });
        setShowSuccessModal(true);
      } else {
        setModalData({
          title: "Save Failed",
          message: "Failed to save positions.",
          details: data.message || data.error || "Please try again."
        });
        setShowErrorModal(true);
      }
    } catch (error) {
      setModalData({
        title: "Connection Error",
        message: "Failed to connect to the server.",
        details: error.message
      });
      setShowErrorModal(true);
    } finally {
      setIsSavingPositions(false);
    }
  };

  return (
    <div className="gd-page">
      <LoadingOverlay isVisible={isLoading || isLoadingPositions} message={isLoading ? "Loading diagram data..." : "Loading saved positions..."} />
      
      <div className="gd-inner">
        <header className="gd-header">
          <h1 className="gd-title">Group & Period Diagram</h1>
          <p className="gd-lead">
            Visual representation of groups, periods, relationships, and forms. Scroll to zoom, left-drag to pan, right-drag to select.
          </p>
          
          <div className="gd-toolbar">
            <button
              type="button"
              className="gd-btn gd-btn--primary"
              onClick={savePositions}
              disabled={isSavingPositions || Object.keys(nodePositions).length === 0}
            >
              {isSavingPositions ? 'Saving…' : 'Save positions'}
            </button>
            <button
              type="button"
              className="gd-btn"
              onClick={() => {
                setNodePositions({});
                saveToHistory({});
              }}
            >
              Reset positions
            </button>
            <button
              type="button"
              className="gd-btn"
              onClick={undo}
              disabled={historyIndex <= 0}
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>
            <button
              type="button"
              className="gd-btn"
              onClick={redo}
              disabled={historyIndex >= positionHistory.length - 1}
              title="Redo (Ctrl+Y)"
            >
              Redo
            </button>
            <button type="button" className="gd-btn" onClick={loadAllData}>
              Refresh data
            </button>
          </div>
        </header>

        {error && (
          <div className="gd-error" role="alert">
            {error}
          </div>
        )}

        {!isLoading && graph.nodes.length === 0 && (
          <div className="gd-empty">
            <h3>No data available</h3>
            <p>No groups or periods found. The diagram will appear once you have:</p>
            <ul style={{ textAlign: 'left', display: 'inline-block' }}>
              <li>At least one group</li>
              <li>At least one period</li>
              <li>At least one group-period relationship</li>
            </ul>
            <p style={{ marginTop: '16px', fontSize: '0.8rem' }}>
              Groups: {allGroups.length} · Periods: {allPeriods.length} · Groups-Periods: {allGroupsPeriods.length} · Relationships: {allRelationships.length} · Forms: {allForms.length}
            </p>
          </div>
        )}

        {!isLoading && graph.nodes.length > 0 && (
        <div className="gd-legend">
          <p className="gd-legend-title">Legend</p>
          <div className="gd-legend-items">
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#3b82f6' }} />
              <span>Groups</span>
            </div>
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#8b5cf6' }} />
              <span>Groups-Periods</span>
            </div>
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#10b981' }} />
              <span>Periods</span>
            </div>
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#f59e0b' }} />
              <span>Forms</span>
            </div>
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#4b5563' }} />
              <span>Form connections</span>
            </div>
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#f59e0b', opacity: 0.7 }} />
              <span>Group relationships</span>
            </div>
            <div className="gd-legend-item">
              <div className="gd-legend-swatch" style={{ backgroundColor: '#3b82f6' }} />
              <span>Group-period links</span>
            </div>
          </div>
        </div>
        )}

        {!isLoading && graph.nodes.length > 0 && (
          <div
            className="gd-canvas-wrap"
            ref={diagramContainerRef}
            onContextMenu={handleDiagramContextMenu}
          >
            <div className="gd-canvas-tools">
              <button
                type="button"
                className="gd-tool-btn"
                onClick={() => setZoomLevel((prev) => clampZoom(prev + 0.1))}
                title="Zoom in"
                aria-label="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                className="gd-tool-btn"
                onClick={() => setZoomLevel((prev) => clampZoom(prev - 0.1))}
                title="Zoom out"
                aria-label="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                className="gd-tool-btn"
                onClick={() => {
                  setZoomLevel(1);
                  setPanOffset({ x: 0, y: 0 });
                }}
                title="Reset view"
                aria-label="Reset zoom and pan"
              >
                ↻
              </button>
              <span className="gd-tool-zoom">{Math.round(zoomLevel * 100)}%</span>
              <div className="gd-tool-divider" aria-hidden />
              <div className="gd-tool-pan">
                <span className="gd-tool-pan-label" title="Pan sensitivity">Pan</span>
                <input
                  type="range"
                  className="gd-tool-pan-slider"
                  min="0.5"
                  max="4"
                  step="0.1"
                  value={panSensitivity}
                  onChange={(e) => setPanSensitivity(parseFloat(e.target.value))}
                  aria-label="Pan sensitivity"
                />
                <span className="gd-tool-pan-value">{panSensitivity.toFixed(1)}×</span>
              </div>
            </div>
            <svg
              ref={svgRef}
              className="gd-svg"
              width="100%"
              height="100%"
              viewBox="0 0 2000 1200"
              preserveAspectRatio="xMidYMid meet"
              onMouseDown={handleSVGMouseDown}
              style={{
                cursor: isAreaSelecting ? 'crosshair' : isPanning ? 'grabbing' : 'grab'
              }}
            >
            <g transform={`translate(${panOffset.x}, ${panOffset.y}) scale(${zoomLevel})`}>
            {/* Area selection rectangle */}
            {isAreaSelecting && (
              <rect
                x={Math.min(areaSelectStart.x, areaSelectEnd.x)}
                y={Math.min(areaSelectStart.y, areaSelectEnd.y)}
                width={Math.abs(areaSelectEnd.x - areaSelectStart.x)}
                height={Math.abs(areaSelectEnd.y - areaSelectStart.y)}
                fill="rgba(59, 130, 246, 0.1)"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeDasharray="5,5"
                pointerEvents="none"
              />
            )}

            {/* Render edges first (so they appear behind nodes) */}
            {graph.edges.map(edge => {
              const sourceNode = graph.nodes.find(n => n.id === edge.source);
              const targetNode = graph.nodes.find(n => n.id === edge.target);
              
              if (!sourceNode || !targetNode) return null;

              // Visual edges (group to groups_periods) are lighter
              // Real relationships (groups_periods to groups_periods) are prominent
              // Form edges are distinct (dark grey)
              // Group-Period Links are thick bright blue
              // Check isGroupPeriod first (even if isVisual is true, group-period links should be bright blue)
              const strokeColor = edge.isForm ? '#4b5563' : edge.isGroupPeriod ? '#3b82f6' : edge.isVisual ? '#94a3b8' : '#f59e0b';
              const strokeWidth = edge.isForm ? 2 : edge.isGroupPeriod ? 3 : edge.isVisual ? 1 : 3;
              const opacity = edge.isForm ? 0.8 : edge.isGroupPeriod ? 1 : edge.isVisual ? 0.4 : 1;

              // Calculate center points
              const sourceCenterX = sourceNode.x + sourceNode.width / 2;
              const sourceCenterY = sourceNode.y + sourceNode.height / 2;
              const targetCenterX = targetNode.x + targetNode.width / 2;
              const targetCenterY = targetNode.y + targetNode.height / 2;

              // Calculate intersection points with container edges
              const getEdgeIntersection = (node, centerX, centerY, targetCenterX, targetCenterY) => {
                const dx = targetCenterX - centerX;
                const dy = targetCenterY - centerY;
                const length = Math.sqrt(dx * dx + dy * dy);
                if (length === 0) return { x: centerX, y: centerY };
                
                const unitX = dx / length;
                const unitY = dy / length;
                
                // Find intersection with node rectangle
                const left = node.x;
                const right = node.x + node.width;
                const top = node.y;
                const bottom = node.y + node.height;
                
                // Calculate intersections with each edge
                let t = Infinity;
                let intersectionX = centerX;
                let intersectionY = centerY;
                
                // Left edge
                if (unitX < 0) {
                  const tLeft = (left - centerX) / unitX;
                  const y = centerY + unitY * tLeft;
                  if (y >= top && y <= bottom && tLeft < t && tLeft > 0) {
                    t = tLeft;
                    intersectionX = left;
                    intersectionY = y;
                  }
                }
                
                // Right edge
                if (unitX > 0) {
                  const tRight = (right - centerX) / unitX;
                  const y = centerY + unitY * tRight;
                  if (y >= top && y <= bottom && tRight < t && tRight > 0) {
                    t = tRight;
                    intersectionX = right;
                    intersectionY = y;
                  }
                }
                
                // Top edge
                if (unitY < 0) {
                  const tTop = (top - centerY) / unitY;
                  const x = centerX + unitX * tTop;
                  if (x >= left && x <= right && tTop < t && tTop > 0) {
                    t = tTop;
                    intersectionX = x;
                    intersectionY = top;
                  }
                }
                
                // Bottom edge
                if (unitY > 0) {
                  const tBottom = (bottom - centerY) / unitY;
                  const x = centerX + unitX * tBottom;
                  if (x >= left && x <= right && tBottom < t && tBottom > 0) {
                    t = tBottom;
                    intersectionX = x;
                    intersectionY = bottom;
                  }
                }
                
                return { x: intersectionX, y: intersectionY };
              };

              const sourceIntersection = getEdgeIntersection(
                sourceNode, sourceCenterX, sourceCenterY, targetCenterX, targetCenterY
              );
              const targetIntersection = getEdgeIntersection(
                targetNode, targetCenterX, targetCenterY, sourceCenterX, sourceCenterY
              );

              return (
                <line
                  key={edge.id}
                  x1={sourceIntersection.x}
                  y1={sourceIntersection.y}
                  x2={targetIntersection.x}
                  y2={targetIntersection.y}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  markerEnd={!edge.isVisual && !edge.isGroupPeriod ? "url(#arrowhead)" : undefined}
                  style={styles.edge}
                  opacity={opacity}
                />
              );
            })}

            {/* Arrow marker for edges */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 10 3, 0 6" fill="#f59e0b" />
              </marker>
            </defs>

            {/* Render nodes */}
            {graph.nodes.map(node => {
              let displayText = '';
              let subText = '';
              let nodeColor = getNodeColor(node.type);

              if (node.type === 'group') {
                displayText = node.data.name;
                subText = node.data.group_type || 'Group';
                nodeColor = '#3b82f6';
              } else if (node.type === 'groups_periods') {
                displayText = node.data.group_name || `Group ${node.groupId}`;
                subText = node.data.period_title || `Period ${node.periodId}`;
                nodeColor = '#8b5cf6'; // Different color for groups_periods
              } else if (node.type === 'form') {
                displayText = node.data.title || `Form ${node.formId}`;
                subText = node.data.type || 'Form';
                nodeColor = '#f59e0b';
              }

              const isDragging = draggingNode === node.id || draggingNodes.has(node.id);
              const isSelected = selectedNodes.has(node.id);
              
              return (
                <g 
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  style={{ cursor: 'move' }}
                  onMouseDown={(e) => handleNodeMouseDown(e, node)}
                >
                  <rect
                    x={0}
                    y={0}
                    width={node.width}
                    height={node.height}
                    fill={nodeColor}
                    stroke={
                      isDragging ? '#fbbf24' : 
                      isSelected ? '#3b82f6' : 
                      selectedNode?.id === node.id ? '#fbbf24' : 
                      '#1e293b'
                    }
                    strokeWidth={isDragging || isSelected || selectedNode?.id === node.id ? 3 : 2}
                    strokeDasharray={node.type === 'group' && node.isContainer ? '5,5' : 'none'}
                    rx="8"
                    style={{...styles.node, opacity: isDragging ? 0.8 : isSelected ? 0.9 : 1}}
                    onClick={(e) => handleNodeClick(node, e)}
                  />
                  <text
                    x={node.width / 2}
                    y={node.height / 2 - (node.type === 'form' ? 4 : 8)}
                    textAnchor="middle"
                    fill="white"
                    fontSize={node.type === 'group' ? "18" : node.type === 'form' ? "14" : "15"}
                    fontWeight="bold"
                    pointerEvents="none"
                  >
                    {displayText.length > 20 ? displayText.substring(0, 17) + '...' : displayText}
                  </text>
                  {/* Period container for groups_periods */}
                  {node.type === 'groups_periods' && (() => {
                    // Calculate container dimensions based on text
                    const padding = 10;
                    const fontSize = 12;
                    const textWidth = subText.length * (fontSize * 0.6); // More accurate character width estimate
                    const containerWidth = Math.min(textWidth + padding * 2, node.width - 10); // Ensure it fits with margin
                    const containerHeight = 20;
                    const containerX = Math.max(5, Math.min(node.width / 2 - containerWidth / 2, node.width - containerWidth - 5)); // Center with bounds check
                    const containerY = Math.max(5, Math.min(node.height / 2 + 2, node.height - containerHeight - 5)); // Ensure it fits within bounds
                    
                    return (
                      <>
                        <rect
                          x={containerX}
                          y={containerY}
                          width={containerWidth}
                          height={containerHeight}
                          fill="#10b981"
                          stroke="#10b981"
                          strokeWidth="2"
                          rx="4"
                          pointerEvents="none"
                        />
                        <text
                          x={containerX + containerWidth / 2}
                          y={containerY + containerHeight / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="white"
                          fontSize={fontSize}
                          fontWeight="600"
                          pointerEvents="none"
                        >
                          {subText}
                        </text>
                      </>
                    );
                  })()}
                  {node.type !== 'groups_periods' && (
                    <text
                      x={node.width / 2}
                      y={node.height / 2 + (node.type === 'form' ? 6 : 10)}
                      textAnchor="middle"
                      fill="white"
                      fontSize={node.type === 'form' ? "11" : "12"}
                      pointerEvents="none"
                    >
                      {subText}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Render connection point circles on top of nodes at edge intersections */}
            {graph.edges.map(edge => {
              const sourceNode = graph.nodes.find(n => n.id === edge.source);
              const targetNode = graph.nodes.find(n => n.id === edge.target);
              
              if (!sourceNode || !targetNode) return null;

              // Check isGroupPeriod first (even if isVisual is true, group-period links should be bright blue)
              const strokeColor = edge.isForm ? '#4b5563' : edge.isGroupPeriod ? '#3b82f6' : edge.isVisual ? '#94a3b8' : '#f59e0b';
              const opacity = edge.isForm ? 0.8 : edge.isGroupPeriod ? 1 : edge.isVisual ? 0.4 : 1;

              // Calculate center points
              const sourceCenterX = sourceNode.x + sourceNode.width / 2;
              const sourceCenterY = sourceNode.y + sourceNode.height / 2;
              const targetCenterX = targetNode.x + targetNode.width / 2;
              const targetCenterY = targetNode.y + targetNode.height / 2;

              // Calculate intersection points with container edges (same logic as edges)
              const getEdgeIntersection = (node, centerX, centerY, targetCenterX, targetCenterY) => {
                const dx = targetCenterX - centerX;
                const dy = targetCenterY - centerY;
                const length = Math.sqrt(dx * dx + dy * dy);
                if (length === 0) return { x: centerX, y: centerY };
                
                const unitX = dx / length;
                const unitY = dy / length;
                
                const left = node.x;
                const right = node.x + node.width;
                const top = node.y;
                const bottom = node.y + node.height;
                
                let t = Infinity;
                let intersectionX = centerX;
                let intersectionY = centerY;
                
                if (unitX < 0) {
                  const tLeft = (left - centerX) / unitX;
                  const y = centerY + unitY * tLeft;
                  if (y >= top && y <= bottom && tLeft < t && tLeft > 0) {
                    t = tLeft;
                    intersectionX = left;
                    intersectionY = y;
                  }
                }
                
                if (unitX > 0) {
                  const tRight = (right - centerX) / unitX;
                  const y = centerY + unitY * tRight;
                  if (y >= top && y <= bottom && tRight < t && tRight > 0) {
                    t = tRight;
                    intersectionX = right;
                    intersectionY = y;
                  }
                }
                
                if (unitY < 0) {
                  const tTop = (top - centerY) / unitY;
                  const x = centerX + unitX * tTop;
                  if (x >= left && x <= right && tTop < t && tTop > 0) {
                    t = tTop;
                    intersectionX = x;
                    intersectionY = top;
                  }
                }
                
                if (unitY > 0) {
                  const tBottom = (bottom - centerY) / unitY;
                  const x = centerX + unitX * tBottom;
                  if (x >= left && x <= right && tBottom < t && tBottom > 0) {
                    t = tBottom;
                    intersectionX = x;
                    intersectionY = bottom;
                  }
                }
                
                return { x: intersectionX, y: intersectionY };
              };

              const sourceIntersection = getEdgeIntersection(
                sourceNode, sourceCenterX, sourceCenterY, targetCenterX, targetCenterY
              );
              const targetIntersection = getEdgeIntersection(
                targetNode, targetCenterX, targetCenterY, sourceCenterX, sourceCenterY
              );

              return (
                <g key={`circles-${edge.id}`}>
                  <circle
                    cx={sourceIntersection.x}
                    cy={sourceIntersection.y}
                    r="5"
                    fill={strokeColor}
                    stroke="white"
                    strokeWidth="2"
                    opacity={opacity}
                    style={{ pointerEvents: 'none' }}
                  />
                  <circle
                    cx={targetIntersection.x}
                    cy={targetIntersection.y}
                    r="5"
                    fill={strokeColor}
                    stroke="white"
                    strokeWidth="2"
                    opacity={opacity}
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              );
            })}
            </g>
          </svg>
          </div>
        )}

        {/* Node details panel */}
        {selectedNode && (
          <div className="gd-details">
            <div className="gd-details-header">
              <h3>
                {selectedNode.type === 'group' ? selectedNode.data.name : 
                 selectedNode.type === 'form' ? selectedNode.data.title :
                 selectedNode.data.title || `Groups-Periods ${selectedNode.groupsPeriodsId}`}
              </h3>
              <button type="button" className="gd-details-close" onClick={() => setSelectedNode(null)} aria-label="Close">×</button>
            </div>
            
            <div className="gd-details-body">
              {selectedNode.type === 'group' ? (
                <>
                  <p><strong>Type:</strong> {selectedNode.data.group_type || 'Group'}</p>
                  <p><strong>Description:</strong> {selectedNode.data.description || 'No description'}</p>
                  <div>
                    <strong>Groups-Periods:</strong>
                    <ul>
                      {allGroupsPeriods
                        .filter(gp => gp.group_id === selectedNode.data.id)
                        .map(gp => {
                          const period = allPeriods.find(p => p.id === gp.period_id);
                          return (
                            <li key={gp.id}>
                              {gp.title || `Group-Period ${gp.id}`} 
                              {period && ` - ${period.title}`}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </>
              ) : selectedNode.type === 'groups_periods' ? (
                <>
                  <p><strong>Groups-Periods ID:</strong> {selectedNode.data.id}</p>
                  <p><strong>Group:</strong> {selectedNode.data.group_name || `Group ${selectedNode.groupId}`}</p>
                  <p><strong>Period:</strong> {selectedNode.data.period_title || `Period ${selectedNode.periodId}`}</p>
                  <p><strong>Title:</strong> {selectedNode.data.title || 'No title'}</p>
                  <div>
                    <strong>Child Groups-Periods (relationships):</strong>
                    <ul>
                      {allRelationships
                        .filter(r => r.parent_table === 'groups_periods' && r.parent_id === selectedNode.groupsPeriodsId)
                        .map(rel => {
                          const childGp = allGroupsPeriods.find(gp => gp.id === rel.child_id);
                          if (!childGp) return null;
                          const period = allPeriods.find(p => p.id === childGp.period_id);
                          return (
                            <li key={rel.id}>
                              {childGp.group_name} - {period?.title || childGp.period_id}
                              {rel.relationship_type && ` (${rel.relationship_type})`}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                  <div>
                    <strong>Parent Groups-Periods (relationships):</strong>
                    <ul>
                      {allRelationships
                        .filter(r => r.child_table === 'groups_periods' && r.child_id === selectedNode.groupsPeriodsId)
                        .map(rel => {
                          const parentGp = allGroupsPeriods.find(gp => gp.id === rel.parent_id);
                          if (!parentGp) return null;
                          const period = allPeriods.find(p => p.id === parentGp.period_id);
                          return (
                            <li key={rel.id}>
                              {parentGp.group_name} - {period?.title || parentGp.period_id}
                              {rel.relationship_type && ` (${rel.relationship_type})`}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                  <div>
                    <strong>Assigned Forms:</strong>
                    {(() => {
                      const forms = getFormsForGroupsPeriods(selectedNode.groupsPeriodsId);
                      if (forms.length === 0) {
                        return <p className="gd-details-muted">No forms assigned</p>;
                      }
                      return (
                        <ul>
                          {forms.map(form => (
                            <li key={form.id}>
                              {form.title}
                              {form.relationship_type && ` - ${form.relationship_type}`}
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                </>
              ) : selectedNode.type === 'form' ? (
                <>
                  <p><strong>Form ID:</strong> {selectedNode.data.id}</p>
                  <p><strong>Title:</strong> {selectedNode.data.title}</p>
                  <p><strong>Type:</strong> {selectedNode.data.type}</p>
                  <p><strong>Questions:</strong> {selectedNode.data.questions?.length || 0}</p>
                  <p><strong>Created:</strong> {new Date(selectedNode.data.created_at).toLocaleDateString()}</p>
                  <div>
                    <strong>Assigned to Groups-Periods:</strong>
                    <ul>
                      {allRelationships
                        .filter(r => r.child_table === 'forms' && r.child_id === selectedNode.formId && r.parent_table === 'groups_periods')
                        .map(rel => {
                          const gp = allGroupsPeriods.find(g => g.id === rel.parent_id);
                          if (!gp) return null;
                          const period = allPeriods.find(p => p.id === gp.period_id);
                          return (
                            <li key={rel.id}>
                              {gp.group_name} - {period?.title || gp.period_id}
                              {rel.relationship_type && ` (${rel.relationship_type})`}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Code:</strong> {selectedNode.data.code}</p>
                  <p><strong>Start Date:</strong> {new Date(selectedNode.data.start_date).toLocaleDateString()}</p>
                  <p><strong>End Date:</strong> {new Date(selectedNode.data.end_date).toLocaleDateString()}</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
        zIndex={2000}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
        zIndex={2000}
      />
    </div>
  );
};

const styles = {
  node: {
    transition: 'all 0.2s ease',
  },
  edge: {
    transition: 'all 0.2s ease',
  },
};

export default GroupDiagram;

